import { useCallback, useRef } from 'react';

import { clampPanelPosition, clampPanelSize, MIN_PANEL_SIZE } from '../utils/panelBounds';

import type { PointerEvent as ReactPointerEvent } from 'react';
import type {
  IDraggablePanelPosition, IDraggablePanelSize,
} from '../interfaces/IDraggablePanel';
import type IDraggablePanelGesture from '../interfaces/IDraggablePanelGesture';
import type { IDraggablePanelGestureOptions } from '../interfaces/IDraggablePanelGesture';

/** Only the primary button drags. A right click on the header belongs to the browser. */
const PRIMARY_BUTTON = 0;

/**
 * Controls that keep their own pointer, rather than handing it to the drag.
 *
 * The header is the drag handle and also holds the close button. Without this, pressing that
 * button would open a gesture, capture the pointer and call `preventDefault`, and the click it
 * was meant to be would never happen.
 */
const CONTROL_SELECTOR = 'button, a, input, select, textarea, [data-no-drag]';

const isOnControl = (target: EventTarget | null): boolean => (
  target instanceof Element && !!target.closest(CONTROL_SELECTOR)
);

/** Custom properties the stylesheet reads the panel geometry from. */
export const PANEL_X = '--panel-x';
export const PANEL_Y = '--panel-y';
export const PANEL_WIDTH = '--panel-width';
export const PANEL_HEIGHT = '--panel-height';

interface IGesture {
  kind: 'move' | 'resize',
  pointerId: number,
  /** Where the pointer went down, which every later position is measured against. */
  pointerX: number,
  pointerY: number,
  /** Where the panel was at that moment, read off the rendered element. */
  position: IDraggablePanelPosition,
  size: IDraggablePanelSize,
  /** What the gesture has come to, kept here so the release can hand it over. */
  latestPosition: IDraggablePanelPosition,
  latestSize: IDraggablePanelSize,
}

const getViewport = (): IDraggablePanelSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * Drags the panel by its header and resizes it by its corner.
 *
 * Every frame of a gesture is written straight to the element as custom properties, so a drag
 * re-renders nothing at all — the browser restyles one box while React sits still. The owner is
 * told once, on release, and its render then agrees with what the panel already shows.
 *
 * The pointer is captured, so a fast drag that outruns the panel keeps going, and a release
 * outside the window still ends the gesture.
 */
export default function useDraggablePanelGesture({
  panelRef,
  maxSize,
  minSize,
  onMove,
  onResize,
}: IDraggablePanelGestureOptions): IDraggablePanelGesture {
  const gestureRef = useRef<IGesture | null>(null);

  const writePosition = useCallback(({ x, y }: IDraggablePanelPosition) => {
    panelRef.current?.style.setProperty(PANEL_X, `${x}px`);
    panelRef.current?.style.setProperty(PANEL_Y, `${y}px`);
  }, [panelRef]);

  const writeSize = useCallback(({ width, height }: IDraggablePanelSize) => {
    panelRef.current?.style.setProperty(PANEL_WIDTH, `${width}px`);
    panelRef.current?.style.setProperty(PANEL_HEIGHT, `${height}px`);
  }, [panelRef]);

  const start = useCallback((kind: IGesture['kind'], event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== PRIMARY_BUTTON || isOnControl(event.target)) {
      return;
    }

    const rect = panelRef.current?.getBoundingClientRect();

    // Measured rather than taken from the props: the panel may be sized by its content, and only
    // the rendered box knows what that came to.
    const position = rect ? { x: rect.left, y: rect.top } : { x: 0, y: 0 };
    const size = rect
      ? { width: rect.width, height: rect.height }
      : minSize ?? MIN_PANEL_SIZE;

    // Stops the drag from selecting the header text on the way past.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    gestureRef.current = {
      kind,
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      position,
      size,
      latestPosition: position,
      latestSize: size,
    };

    const panel = panelRef.current;

    if (panel) {
      panel.dataset.gesturing = kind;
    }

    // A panel sized by its content has no width to grow from until one is written.
    if (kind === 'resize') {
      writeSize(size);
    }
  }, [minSize, panelRef, writeSize]);

  const startMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => start('move', event),
    [start],
  );

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => start('resize', event),
    [start],
  );

  const updateGesture = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;

    if (!gesture || gesture.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - gesture.pointerX;
    const deltaY = event.clientY - gesture.pointerY;
    const viewport = getViewport();

    if (gesture.kind === 'move') {
      gesture.latestPosition = clampPanelPosition(
        { x: gesture.position.x + deltaX, y: gesture.position.y + deltaY },
        gesture.size,
        viewport,
      );
      writePosition(gesture.latestPosition);

      return;
    }

    gesture.latestSize = clampPanelSize(
      { width: gesture.size.width + deltaX, height: gesture.size.height + deltaY },
      gesture.position,
      viewport,
      maxSize,
      minSize,
    );
    writeSize(gesture.latestSize);
  }, [maxSize, minSize, writePosition, writeSize]);

  const endGesture = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;

    if (!gesture || gesture.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    gestureRef.current = null;

    const panel = panelRef.current;

    if (panel) {
      delete panel.dataset.gesturing;
    }

    // The element already shows this. Telling the owner keeps its state in step, and its render
    // writes the same values back over the ones the gesture left.
    if (gesture.kind === 'move') {
      onMove?.(gesture.latestPosition);
    } else {
      onResize?.(gesture.latestSize);
    }
  }, [onMove, onResize, panelRef]);

  return {
    startMove, startResize, updateGesture, endGesture,
  };
}
